/**
 * Pure autosave decision helpers for the Report Builder editor.
 *
 * These exist as standalone functions (rather than inline in the provider)
 * so the autosave state machine can be unit-tested without mounting the
 * docx editor or the React context. The docx editor fires `onChange` for
 * non-content mutations (`setZoom` on every ResizeObserver tick / column
 * toggle, selection, layout/pagination); the signature comparison here is
 * what stops those from triggering a save (which kept resetting `lastSavedAt`
 * and looping the "Saving…/Autosaved just now" label).
 */

/**
 * Cheap content fingerprint: `size:text`. Size catches structural edits,
 * textContent catches text edits — enough to distinguish a real edit from a
 * zoom/selection event. `null` doc (editor not mounted) → empty string.
 */
export const buildDocSignature = (
  doc: { content: { size: number }; textContent: string } | null | undefined
): string => (doc ? `${doc.content.size}:${doc.textContent}` : "");

/** ProseMirror node types whose binary the docx-editor's serializer drops
 *  on a save round-trip. The node reference survives in the saved XML but
 *  the underlying picture bytes don't end up in the zip's `word/media/`,
 *  so the next load renders an empty `<img>`. Skip the .docx upload
 *  whenever any of these are in the doc.
 *
 *  Verified against @eigenpal/docx-editor-core's bundled schema: `image`
 *  and `drawing` are the two node names that wrap a `<w:drawing>` /
 *  `<w:pict>` and rely on a binary in `word/media/`. Other media-shaped
 *  nodes (`shape`, `textBox`, `math`) don't reference an external binary
 *  in the same way and aren't included here; widen the set if QA reports
 *  a non-image embed losing its visuals on round-trip. */
export const MEDIA_NODE_TYPES = new Set(["image", "drawing"]);

/** True when the doc contains any media node the serializer can't preserve.
 *  Walks once and short-circuits on the first match. Accepts a minimal
 *  structural type so it can be unit-tested without spinning up a real
 *  ProseMirror state. */
export const docContainsMedia = (
  doc:
    | {
        descendants: (
          cb: (node: { type: { name: string } }) => boolean | void
        ) => void;
      }
    | null
    | undefined
): boolean => {
  if (!doc) return false;
  let found = false;
  doc.descendants((node) => {
    if (found) return false;
    if (MEDIA_NODE_TYPES.has(node.type.name)) {
      found = true;
      return false;
    }
  });
  return found;
};

export type EditChangeOutcome =
  /** First change after load — establish baseline, not a user edit. */
  | { kind: "baseline"; signature: string }
  /** Non-content mutation (zoom/selection/layout) — ignore. */
  | { kind: "ignore" }
  /** Real content edit — mark dirty + reschedule autosave. */
  | { kind: "edit"; signature: string };

/**
 * Decide what an `onChange` means, given the current doc signature and the
 * last-saved baseline (`null` until the first change after load).
 */
export const classifyEditChange = (
  signature: string,
  lastSavedSignature: string | null
): EditChangeOutcome => {
  if (lastSavedSignature === null) return { kind: "baseline", signature };
  if (signature === lastSavedSignature) return { kind: "ignore" };
  return { kind: "edit", signature };
};

/** Clamp a candidate Notes-panel width to its allowed range. */
export const clampNotesWidth = (next: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, next));

/**
 * Maximum Notes-panel width: half of the main container. Floored at `min` so
 * the clamp range stays valid on narrow containers (or a 0 width before the
 * container is measured), where half the width would fall below the minimum.
 */
export const notesMaxWidth = (containerWidth: number, min: number): number =>
  Math.max(min, containerWidth * 0.5);

/**
 * Relative "Autosaved …" label for a save timestamp `ts` given the current
 * time `now` (both ms). `null` ts → "" (never saved). Sub-minute → "just now".
 */
export const formatRelative = (ts: number | null, now: number): string => {
  if (!ts) return "";
  const mins = Math.floor((now - ts) / 60000);
  if (mins <= 0) return "just now";
  if (mins === 1) return "1 min ago";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  return hrs === 1 ? "1 hour ago" : `${hrs} hours ago`;
};
